import React, { useState, useEffect } from 'react';
// Service role client calls replaced with API endpoints
import { getUserIdFromObject, assertValidUserId, extractNumericId } from '../lib/userIdExtractor';
import { isMasterAdminViewSession } from '../lib/masterAdminAccess';
import { getSessionHeaders } from '../lib/serverSession';
import WalletQRCode from './user/WalletQRCode';
import QRCode from 'qrcode';
import { ReferralAutocomplete } from './ReferralAutocomplete';
import PurchaseRewardPreview from './PurchaseRewardPreview';

interface PaymentNetwork {
  id: string;
  name: string;
  technical: string;
  icon: string;
  wallet_address: string;
}

interface CurrentPhase {
  id: number;
  phase_number: number;
  phase_name: string;
  price_per_share: number;
  total_shares_available: number;
  shares_sold: number;
  is_active: boolean;
}

interface User {
  id: number;
  email: string;
  username?: string;
  full_name?: string;
  database_user?: {
    username?: string;
    full_name?: string;
  };
}

interface CryptoPaymentStepProps {
  amount: string;
  shares: number;
  network: PaymentNetwork;
  currentPhase: CurrentPhase | null;
  user: User | null;
  onBack: () => void;
  onComplete: () => void;
  useCommission?: boolean;
  commissionBalance?: number;
  commissionAmountToUse?: string;
  bonusShares?: number;
  totalShares?: number;
  // Resume/resubmit payment support
  existingPaymentId?: string; // ID of existing payment to update (for resume)
  isResubmit?: boolean; // If true, create new record even if existingPaymentId provided
  // Balance payment support (for underpaid payments)
  isBalancePayment?: boolean; // If true, this is a balance payment for an underpaid transaction
  originalPaymentId?: string; // ID of the original underpaid payment
  balanceOwed?: number; // The exact balance amount owed
  // Birthday 2026 package metadata (carried into transaction_notes for approval)
  birthdayPackage?: 'BIRTHDAY_2' | 'BIRTHDAY_25';
  birthdayQty?: number;
  // Production Capital Share Campaign package (P1–P5)
  productionCapitalPackage?: 'P1' | 'P2' | 'P3' | 'P4' | 'P5';
  productionCapitalPackageQty?: number;
  productionCapitalPackages?: Array<{ packageId: 'P1' | 'P2' | 'P3' | 'P4' | 'P5'; qty: number }>;
  singleShareActive?: boolean;
}

const CryptoPaymentStep: React.FC<CryptoPaymentStepProps> = ({
  amount,
  shares,
  network,
  currentPhase,
  user,
  onBack,
  onComplete,
  useCommission = false,
  commissionAmountToUse = '',
  bonusShares = 0,
  totalShares = 0,
  isBalancePayment = false,
  originalPaymentId,
  balanceOwed,
  birthdayPackage,
  birthdayQty,
  productionCapitalPackage,
  productionCapitalPackageQty,
  productionCapitalPackages,
  singleShareActive = false,
}) => {

  // Transaction state
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);
  const [error, setError] = useState('');
  const [_proofFile, setProofFile] = useState<File | null>(null);
  const [uploadProgress, setUploadProgress] = useState('');

  // Manual payment fields
  const [senderWallet, setSenderWallet] = useState('');
  const [manualTxHash, setManualTxHash] = useState('');

  // Confirmation dialog state
  const [showConfirmation, setShowConfirmation] = useState(false);

  // Share QR generation state
  const [isGeneratingShareQR, setIsGeneratingShareQR] = useState(false);

  // Copy feedback state
  const [walletCopied, setWalletCopied] = useState(false);

  // v7.3.345: Hover state for Submit Payment button (restores active/hover affordance)
  const [isSubmitHovered, setIsSubmitHovered] = useState(false);

  // Gift purchase state
  const [isGiftPurchase, setIsGiftPurchase] = useState(false);
  const [recipientUsername, setRecipientUsername] = useState('');
  const [recipientValidation, setRecipientValidation] = useState<{
    isValidating: boolean;
    isValid: boolean | null;
    recipientName: string | null;
    recipientId: number | null;
  }>({
    isValidating: false,
    isValid: null,
    recipientName: null,
    recipientId: null
  });

  // v4.8.115: Multi-company purchaser-capacity state (Phase 3 — frontend). Lets a
  // primary director choose to buy in COMPANY capacity. PERSONAL is the default and
  // sends NOTHING extra to the API (byte-identical to before). Company and gift are
  // mutually exclusive (a company buys for its own holdings, it does not gift). The
  // client selection is a convenience only — the backend (Phase 2) re-validates
  // primary-director/KYB/operational status server-side and is authoritative.
  type PurchaserCapacity = 'PERSONAL' | 'COMPANY';
  interface EligibleCompany {
    id: string;
    legal_name: string;
    kyc_status?: string | null;
    operational_status?: string | null;
    is_frozen?: boolean | null;
  }
  const [purchaserType, setPurchaserType] = useState<PurchaserCapacity>('PERSONAL');
  const [companies, setCompanies] = useState<EligibleCompany[]>([]);
  const [selectedCompanyId, setSelectedCompanyId] = useState<string>('');
  const [companiesLoading, setCompaniesLoading] = useState<boolean>(false);

  // A company is eligible to purchase only when it is KYB-approved, operationally
  // active, and not frozen. This mirrors the fail-closed server-side gate so the UI
  // never offers a company the backend will reject.
  const isCompanyEligible = (c: EligibleCompany): boolean =>
    String(c.kyc_status || '').toLowerCase() === 'approved' &&
    String(c.operational_status || 'active').toLowerCase() === 'active' &&
    c.is_frozen !== true;

  // v4.3.60: Strip spaces and special characters on input — only allow valid hex/alphanumeric
  const handleSenderWalletChange = (value: string) => {
    // Strip all whitespace and non-alphanumeric chars (except 0x prefix)
    const cleaned = value.replace(/\s+/g, '');
    setSenderWallet(cleaned);
    if (error) setError('');
  };

  const handleTxHashChange = (value: string) => {
    // Strip all whitespace and non-alphanumeric chars (except 0x prefix)
    const cleaned = value.replace(/\s+/g, '');
    setManualTxHash(cleaned);
    if (error) setError('');
  };

  // Validate wallet address format
  const isValidWalletAddress = (addr: string): boolean => {
    const trimmed = addr.trim();
    if (!trimmed) return false;
    // EVM: 0x + 40 hex chars
    if (/^0x[a-fA-F0-9]{40}$/.test(trimmed)) return true;
    // TRON: T + 33 alphanumeric
    if (/^T[a-zA-Z0-9]{33}$/.test(trimmed)) return true;
    return false;
  };

  // Validate transaction hash format
  const isValidTxHash = (hash: string): boolean => {
    const trimmed = hash.trim();
    if (!trimmed) return false;
    // Standard EVM: 0x + 64 hex chars
    if (/^0x[a-fA-F0-9]{64}$/.test(trimmed)) return true;
    // Hex without prefix: 64 hex chars
    if (/^[a-fA-F0-9]{64}$/.test(trimmed)) return true;
    return false;
  };

  // SPECIAL PRICING: User 106 gets $1 for exactly 1 share (testing)
  const TEST_USER_ID = 106;
  const TEST_PRICE_SINGLE_SHARE = 1;

  // Extract user ID for special pricing using centralized utility
  const numericUserId = extractNumericId((user?.database_user as any)?.id)
    || extractNumericId((user as any)?.user_metadata?.user_id)
    || extractNumericId(user?.id);

  const isSpecialPricingActive = numericUserId === TEST_USER_ID && shares === 1;

  // v4.8.115: Load the companies where this user is the primary director so a
  // director can buy in COMPANY capacity. Read-only fetch; failure is non-fatal
  // (the selector simply stays empty and the personal flow is unaffected).
  useEffect(() => {
    let cancelled = false;
    const loadCompanies = async () => {
      if (!numericUserId) return;
      setCompaniesLoading(true);
      try {
        const resp = await fetch('/api/user/company', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...getSessionHeaders() },
          body: JSON.stringify({ userId: numericUserId, operation: 'get_user_companies' })
        });
        const json = await resp.json();
        const list: EligibleCompany[] = Array.isArray(json?.data)
          ? json.data
          : (Array.isArray(json) ? json : []);
        if (!cancelled) setCompanies(list);
      } catch (e) {
        if (!cancelled) setCompanies([]);
        console.warn('CRYPTO PAYMENT: could not load company profiles:', (e as any)?.message || e);
      } finally {
        if (!cancelled) setCompaniesLoading(false);
      }
    };
    loadCompanies();
    return () => { cancelled = true; };
  }, [numericUserId]);

  // Calculate total cost with special pricing check
  const normalCost = shares * (currentPhase?.price_per_share || 0);
  const totalCost = isSpecialPricingActive ? TEST_PRICE_SINGLE_SHARE : normalCost;

  // Calculate commission application
  const commissionApplied = useCommission && commissionAmountToUse && parseFloat(commissionAmountToUse) > 0
    ? Math.min(parseFloat(commissionAmountToUse), totalCost)
    : 0;

  const remainingPayment = Math.max(0, totalCost - commissionApplied);

  console.log('💳 Crypto Payment - Special Pricing Check:', {
    userId: numericUserId,
    shares,
    isSpecialPricingActive,
    normalCost,
    totalCost,
    remainingPayment
  });

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    setWalletCopied(true);
    setTimeout(() => setWalletCopied(false), 2000);
  };

  // Generate a branded shareable payment QR code image
  const generateShareablePaymentQR = async () => {
    if (isGeneratingShareQR) return;
    setIsGeneratingShareQR(true);

    try {
      const W = 600;
      const H = 820;
      const canvas = document.createElement('canvas');
      canvas.width = W;
      canvas.height = H;
      const ctx = canvas.getContext('2d')!;

      // Background gradient
      const bg = ctx.createLinearGradient(0, 0, 0, H);
      bg.addColorStop(0, '#0f172a');
      bg.addColorStop(1, '#1e293b');
      ctx.fillStyle = bg;
      ctx.fillRect(0, 0, W, H);

      // Gold border
      ctx.strokeStyle = '#d4af37';
      ctx.lineWidth = 3;
      ctx.strokeRect(8, 8, W - 16, H - 16);

      // Header
      ctx.fillStyle = '#d4af37';
      ctx.font = 'bold 22px Arial, sans-serif';
      ctx.textAlign = 'center';
      ctx.fillText('Aureus Africa', W / 2, 50);
      ctx.font = '16px Arial, sans-serif';
      ctx.fillStyle = '#94a3b8';
      ctx.fillText('Share Purchase Payment', W / 2, 76);

      // Divider
      ctx.strokeStyle = '#d4af3766';
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(40, 94);
      ctx.lineTo(W - 40, 94);
      ctx.stroke();

      // Network badge
      ctx.fillStyle = '#1e3a5f';
      const badgeText = `Network: ${network.technical}`;
      ctx.font = 'bold 14px Arial, sans-serif';
      const badgeW = ctx.measureText(badgeText).width + 32;
      const badgeX = (W - badgeW) / 2;
      ctx.beginPath();
      ctx.roundRect(badgeX, 108, badgeW, 32, 16);
      ctx.fill();
      ctx.fillStyle = '#60a5fa';
      ctx.textAlign = 'center';
      ctx.fillText(badgeText, W / 2, 130);

      // Amount
      ctx.fillStyle = '#22c55e';
      ctx.font = 'bold 36px Arial, sans-serif';
      ctx.fillText(`$${remainingPayment.toFixed(2)} USDT`, W / 2, 190);

      // QR code
      const qrSize = 280;
      const qrDataUrl = await QRCode.toDataURL(network.wallet_address, {
        errorCorrectionLevel: 'H',
        margin: 2,
        width: qrSize,
        color: { dark: '#000000', light: '#ffffff' },
      });

      const qrImg = new Image();
      await new Promise<void>((resolve, reject) => {
        qrImg.onload = () => resolve();
        qrImg.onerror = reject;
        qrImg.src = qrDataUrl;
      });

      // QR background box
      const qrBoxPad = 16;
      ctx.fillStyle = '#111827';
      ctx.strokeStyle = '#d4af3766';
      ctx.lineWidth = 2;
      const qrBoxX = (W - qrSize) / 2 - qrBoxPad;
      const qrBoxY = 216 - qrBoxPad;
      const qrBoxW = qrSize + qrBoxPad * 2;
      const qrBoxH = qrSize + qrBoxPad * 2;
      ctx.beginPath();
      ctx.roundRect(qrBoxX, qrBoxY, qrBoxW, qrBoxH, 12);
      ctx.fill();
      ctx.stroke();

      ctx.drawImage(qrImg, (W - qrSize) / 2, 216, qrSize, qrSize);

      // "Scan to pay" label
      ctx.fillStyle = '#94a3b8';
      ctx.font = '13px Arial, sans-serif';
      ctx.fillText('Scan with your wallet app', W / 2, 528);

      // Wallet address
      ctx.fillStyle = '#d4af37';
      ctx.font = 'bold 14px Arial, sans-serif';
      ctx.fillText('Wallet Address:', W / 2, 566);

      ctx.fillStyle = '#ffffff';
      ctx.font = '12px monospace';
      const addr = network.wallet_address;
      if (addr.length > 42) {
        ctx.fillText(addr.substring(0, 42), W / 2, 590);
        ctx.fillText(addr.substring(42), W / 2, 608);
      } else {
        ctx.fillText(addr, W / 2, 590);
      }

      // Shares info
      const sharesText = bonusShares > 0
        ? `${shares} Shares (+${bonusShares} bonus = ${totalShares} total)`
        : `${shares} Share${shares !== 1 ? 's' : ''}`;
      ctx.fillStyle = '#fbbf24';
      ctx.font = '14px Arial, sans-serif';
      ctx.fillText(sharesText, W / 2, 640);

      // Footer divider
      ctx.strokeStyle = '#d4af3766';
      ctx.beginPath();
      ctx.moveTo(40, 670);
      ctx.lineTo(W - 40, 670);
      ctx.stroke();

      // Footer
      ctx.fillStyle = '#64748b';
      ctx.font = '11px Arial, sans-serif';
      ctx.fillText('aureus.africa', W / 2, 696);
      ctx.fillText('Verify payment details before sending', W / 2, 716);

      // Phase info
      if (currentPhase) {
        ctx.fillStyle = '#475569';
        ctx.font = '11px Arial, sans-serif';
        ctx.fillText(`${currentPhase.phase_name} — $${currentPhase.price_per_share}/share`, W / 2, 740);
      }

      // Convert to blob and share/download
      canvas.toBlob(async (blob) => {
        if (!blob) {
          setIsGeneratingShareQR(false);
          return;
        }

        // Try Web Share API (mobile)
        if (navigator.share && navigator.canShare) {
          const file = new File([blob], 'aureus-payment-qr.png', { type: 'image/png' });
          const shareData = { title: 'Aureus Africa Payment', files: [file] };
          if (navigator.canShare(shareData)) {
            try {
              await navigator.share(shareData);
              setIsGeneratingShareQR(false);
              return;
            } catch (e) {
              // User cancelled or share failed — fall through to download
            }
          }
        }

        // Fallback: download
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'aureus-payment-qr.png';
        a.click();
        URL.revokeObjectURL(url);
        setIsGeneratingShareQR(false);
      }, 'image/png');
    } catch (err) {
      console.error('Failed to generate shareable QR:', err);
      setIsGeneratingShareQR(false);
    }
  };

  // BSC Auto-Verification Trigger (async, non-blocking)
  const triggerBscVerification = async (
    paymentId: string,
    txHash: string,
    userId: number,
    expectedAmount: number,
    senderWallet: string,
    companyWallet: string
  ) => {
    const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '';

    try {
      console.log('🔍 BSC VERIFY: Triggering verification for payment:', paymentId);

      const response = await fetch(`${API_BASE_URL}/api/verify-bsc-transaction`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getSessionHeaders()
        },
        body: JSON.stringify({
          paymentId,
          txHash,
          userId,
          expectedAmount,
          senderWallet,
          companyWallet
        })
      });

      const result = await response.json();

      if (result.success) {
        console.log('✅ BSC VERIFY: Transaction verified successfully!', result.transactionData);
      } else if (result.status === 'pending_confirmations') {
        console.log('⏳ BSC VERIFY: Awaiting confirmations...', result.error);
      } else if (result.status === 'manual_review') {
        console.log('👀 BSC VERIFY: Flagged for manual review:', result.error);
      } else {
        console.log('❌ BSC VERIFY: Verification failed:', result.error);
      }

      return result;
    } catch (error) {
      console.error('❌ BSC VERIFY: API call failed:', error);
      throw error;
    }
  };

  // Validate recipient username
  const validateRecipientUsername = async (username: string) => {
    if (!username.trim()) {
      setRecipientValidation({
        isValidating: false,
        isValid: null,
        recipientName: null,
        recipientId: null
      });
      return;
    }

    setRecipientValidation({
      isValidating: true,
      isValid: null,
      recipientName: null,
      recipientId: null
    });

    try {
      // Use API endpoint to validate recipient (bypasses RLS server-side)
      const response = await fetch('/api/purchase/validate-recipient', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getSessionHeaders() },
        body: JSON.stringify({ username: username.trim() })
      });

      const result = await response.json();

      if (!response.ok || !result.success) {
        console.error('Error validating recipient username:', result.error);
        setRecipientValidation({
          isValidating: false,
          isValid: false,
          recipientName: null,
          recipientId: null
        });
        return;
      }

      if (!result.isValid) {
        // v2.9.103 + v4.0.47: Show specific message for self-purchase or sponsor-purchase attempt
        const selfPurchaseMsg = result.isSelf
          ? 'You cannot purchase shares for yourself. Simply purchase shares normally instead.'
          : result.isSponsor
          ? 'You cannot purchase shares for your sponsor or any upline sponsor. The shares would be assigned to them instead of you.'
          : null;
        setRecipientValidation({
          isValidating: false,
          isValid: false,
          recipientName: selfPurchaseMsg,
          recipientId: null
        });
      } else {
        setRecipientValidation({
          isValidating: false,
          isValid: true,
          recipientName: result.recipientName,
          recipientId: result.recipientId
        });
      }
    } catch (error) {
      console.error('Error validating recipient username:', error);
      setRecipientValidation({
        isValidating: false,
        isValid: false,
        recipientName: null,
        recipientId: null
      });
    }
  };

  // Handle recipient username change with debounce
  const handleRecipientUsernameChange = (value: string) => {
    setRecipientUsername(value);

    // Debounce validation
    const timeoutId = setTimeout(() => {
      validateRecipientUsername(value);
    }, 500);

    return () => clearTimeout(timeoutId);
  };

  // Compress image before upload to improve speed
  const compressImage = async (file: File): Promise<File> => {
    if (!file.type.startsWith('image/')) {
      return file;
    }

    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement('canvas');
          let width = img.width;
          let height = img.height;

          const maxWidth = 1920;
          if (width > maxWidth) {
            height = (height * maxWidth) / width;
            width = maxWidth;
          }

          canvas.width = width;
          canvas.height = height;

          const ctx = canvas.getContext('2d');
          ctx?.drawImage(img, 0, 0, width, height);

          canvas.toBlob(
            (blob) => {
              if (blob) {
                const compressedFile = new File([blob], file.name, {
                  type: 'image/jpeg',
                  lastModified: Date.now()
                });
                resolve(compressedFile);
              } else {
                resolve(file);
              }
            },
            'image/jpeg',
            0.8
          );
        };
        img.src = e.target?.result as string;
      };
      reader.readAsDataURL(file);
    });
  };

  const __handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
      if (!allowedTypes.includes(file.type)) {
        setError('Please upload a valid image file (JPG, PNG, WEBP)');
        return;
      }

      if (file.size > 5 * 1024 * 1024) {
        setError('File size must be less than 5MB');
        return;
      }

      setUploadProgress('Preparing file...');
      try {
        const processedFile = await compressImage(file);
        setProofFile(processedFile);
        setError('');
        setUploadProgress('');
      } catch (err) {
        console.error('File processing error:', err);
        setProofFile(file);
        setError('');
        setUploadProgress('');
      }
    }
  };

  // Show confirmation dialog
  const handleShowConfirmation = () => {
    console.log('🔍 CRYPTO PAYMENT: Validating inputs before showing confirmation');

    if (!senderWallet.trim()) {
      console.log('❌ CRYPTO PAYMENT: Missing sender wallet');
      setError('Please enter your wallet address');
      return;
    }

    // v4.3.60: Validate wallet address format — no spaces, must be valid hex address
    if (!isValidWalletAddress(senderWallet.trim())) {
      console.log('❌ CRYPTO PAYMENT: Invalid wallet address format');
      setError('Invalid wallet address. Must be a valid blockchain address (e.g., 0x... for BSC/ETH or T... for TRON). No spaces or special characters allowed.');
      return;
    }

    // v4.3.61: Prevent user from entering the company wallet address as their own
    if (network?.wallet_address && senderWallet.trim().toLowerCase() === network.wallet_address.toLowerCase()) {
      console.log('❌ CRYPTO PAYMENT: User entered company wallet as sender');
      setError('This is the company receiving wallet address. Please enter YOUR wallet address — the one you sent the payment FROM.');
      return;
    }

    if (!manualTxHash.trim()) {
      console.log('❌ CRYPTO PAYMENT: Missing transaction hash');
      setError('Please enter the transaction hash');
      return;
    }

    // v4.3.60: Validate transaction hash format — no spaces, must be valid hex
    if (!isValidTxHash(manualTxHash.trim())) {
      console.log('❌ CRYPTO PAYMENT: Invalid transaction hash format');
      setError('Invalid transaction hash. Must be a valid hex hash (e.g., 0x followed by 64 hex characters). No spaces or special characters allowed.');
      return;
    }

    if (!user) {
      console.log('❌ CRYPTO PAYMENT: No user object');
      setError('User not found. Please log in again.');
      return;
    }

    // Validate gift purchase if enabled
    if (isGiftPurchase) {
      if (!recipientUsername.trim()) {
        console.log('❌ CRYPTO PAYMENT: Missing recipient username');
        setError('Please enter the recipient username');
        return;
      }

      if (recipientValidation.isValid !== true) {
        console.log('❌ CRYPTO PAYMENT: Invalid recipient username');
        setError(`Username "${recipientUsername}" not found. Please check the spelling and try again.`);
        return;
      }

      console.log('✅ CRYPTO PAYMENT: Gift purchase validated for:', recipientValidation.recipientName);
    }

    // v4.8.115: Validate COMPANY-capacity purchase (client-side convenience gate;
    // the backend re-validates and is authoritative). Company and gift are mutually
    // exclusive, and an eligible company must be selected.
    if (purchaserType === 'COMPANY') {
      if (isGiftPurchase) {
        setError('A company purchase cannot also be a gift. Please choose one capacity.');
        return;
      }
      const chosen = companies.find(c => c.id === selectedCompanyId);
      if (!chosen) {
        setError('Please select the company you are purchasing for.');
        return;
      }
      if (!isCompanyEligible(chosen)) {
        setError('This company is not eligible to purchase (it must be KYB-approved, active, and not frozen).');
        return;
      }
      console.log('✅ CRYPTO PAYMENT: Company purchase validated for:', chosen.legal_name);
    }

    console.log('✅ CRYPTO PAYMENT: Validation passed, showing confirmation');
    setError('');
    setShowConfirmation(true);
  };

  // Actual payment submission (called after confirmation)
  const handleManualPayment = async () => {
    // 🔒 ADMIN SAFETY: Block actual submission for master admin testing sessions
    if (isMasterAdminViewSession()) {
      console.log('🛑 ADMIN TEST MODE: Payment submission blocked — admin testing only');
      setShowConfirmation(false);
      setError('🛑 Admin Test Mode: Payment submission is disabled. You have successfully tested the full purchase flow up to the confirmation screen.');
      return;
    }

    console.log('🚀 CRYPTO PAYMENT: Starting manual payment submission');
    console.log('🚀 CRYPTO PAYMENT: User confirmed purchase');
    console.log('🚀 CRYPTO PAYMENT: User object:', user);
    console.log('🚀 CRYPTO PAYMENT: Sender wallet:', senderWallet.trim());
    console.log('🚀 CRYPTO PAYMENT: Transaction hash:', manualTxHash.trim());
    console.log('🚀 CRYPTO PAYMENT: Network:', network);
    console.log('🚀 CRYPTO PAYMENT: Amount:', totalCost);
    console.log('🚀 CRYPTO PAYMENT: Shares:', shares);

    setShowConfirmation(false);
    setIsProcessingPayment(true);
    setError('');
    setUploadProgress('Processing payment...');

    try {
      const screenshotUrl = null; // Proof of payment upload removed

      console.log('🚀 CRYPTO PAYMENT: Getting user ID...');
      // Get user ID using centralized utility
      const userId = await getUserIdFromObject(user);
      console.log('🚀 CRYPTO PAYMENT: User ID result:', userId);

      if (!userId) {
        throw new Error('User not found. Please ensure you are properly logged in.');
      }

      // Validate that we have a proper integer user ID
      assertValidUserId(userId, 'crypto payment creation');

      console.log('🚀 CRYPTO PAYMENT: Creating payment record...');
      // Create payment record with manual transaction details and screenshot URL
      // v2.9.94: Extract valid hex hash from user input (strips prefixed text like "Paid 0x...")
      // v2.9.95: cleanHash now applies .toLowerCase() for consistent case normalization
      const cleanHash = (str: string): string => {
        const stripped = str.replace(/\s+/g, '');
        const evmMatch = stripped.match(/0x[a-fA-F0-9]{64}/);
        if (evmMatch) return evmMatch[0].toLowerCase();
        const hexMatch = stripped.match(/[a-fA-F0-9]{64}/);
        if (hexMatch) return hexMatch[0].toLowerCase();
        return stripped;
      };
      await createPaymentRecord(userId, cleanHash(manualTxHash), cleanHash(senderWallet), screenshotUrl ?? undefined);

      console.log('✅ CRYPTO PAYMENT: Manual payment submitted successfully');

      // Complete the payment flow
      onComplete();

    } catch (error: any) {
      console.error('❌ CRYPTO PAYMENT: Manual payment submission failed:', error?.message || String(error));

      // Set error message FIRST before any other logging
      let errorMessage = '';

      // Check if this is the "statement is not a function" error
      if (error?.message && error.message.includes('statement')) {
        errorMessage = 'System error detected. Please refresh the page and try again. If the problem persists, contact support.';
      } else {
        errorMessage = error?.message || 'Payment submission failed. Please try again.';
      }

      console.log('🔴 CRYPTO PAYMENT: Setting error message:', errorMessage);
      setError(errorMessage);
      console.log('🔴 CRYPTO PAYMENT: Error state set successfully');
    } finally {
      setIsProcessingPayment(false);
      setUploadProgress('');
    }
  };





  const createPaymentRecord = async (_userId: number, txHash: string, manualSenderWallet: string, screenshotUrl?: string) => {
    console.log('💾 CRYPTO PAYMENT: Creating payment record for manual transaction');

    // Check if this is an admin-assisted purchase
    const { getMasterAdminViewSession } = await import('../lib/masterAdminAccess');
    const adminSession = getMasterAdminViewSession();
    const isAdminAssisted = adminSession && adminSession.permissions?.canPurchase;

    if (isAdminAssisted) {
      console.log('🤝 CRYPTO PAYMENT: Admin-assisted purchase detected');
      console.log('🤝 CRYPTO PAYMENT: Admin:', adminSession.adminEmail);
      console.log('🤝 CRYPTO PAYMENT: Helping user:', adminSession.targetEmail);
    }

    // Determine recipient user ID (for gift purchases)
    // CRITICAL: Must use numericUserId (integer) not raw userId (could be UUID)
    // referrals.referred_id and commission_balances.user_id are integer columns
    if (!numericUserId) {
      setError('Unable to verify your account. Please log in again.');
      setIsProcessingPayment(false);
      return;
    }
    const recipientUserId = isGiftPurchase && recipientValidation.recipientId
      ? recipientValidation.recipientId
      : numericUserId;

    const purchaserUserId = numericUserId; // The person making the payment (must be integer)

    if (isGiftPurchase) {
      console.log('🎁 CRYPTO PAYMENT: Gift purchase detected');
      console.log('🎁 CRYPTO PAYMENT: Purchaser ID:', purchaserUserId);
      console.log('🎁 CRYPTO PAYMENT: Recipient ID:', recipientUserId);
      console.log('🎁 CRYPTO PAYMENT: Recipient:', recipientValidation.recipientName);
    }

    // CRITICAL VALIDATION: Verify share calculation is correct
    if (!currentPhase) {
      throw new Error('CRITICAL ERROR: No phase data available. Please refresh the page and try again.');
    }

    const expectedShares = Math.floor(parseFloat(amount) / currentPhase.price_per_share);
    const overpayment = parseFloat(amount) - (expectedShares * currentPhase.price_per_share);
    if (shares !== expectedShares) {
      console.error('❌ CRITICAL: Share calculation mismatch!');
      console.error('Amount:', amount);
      console.error('Phase price:', currentPhase.price_per_share);
      console.error('Expected shares:', expectedShares);
      console.error('Received shares:', shares);
      throw new Error(`CRITICAL ERROR: Share calculation mismatch. Expected ${expectedShares} shares but got ${shares}. Please refresh the page and try again.`);
    }
    if (overpayment >= 0.01) {
      console.log(`💵 CRYPTO PAYMENT: Overpayment of $${overpayment.toFixed(2)} will be credited to USDT commission balance after verification`);
    }

    console.log('💾 CRYPTO PAYMENT: Payment data details:', {
      user_id: recipientUserId, // Shares go to recipient
      amount: totalCost,
      shares_to_purchase: shares,
      phase_name: currentPhase.phase_name,
      phase_price: currentPhase.price_per_share,
      calculated_shares: expectedShares,
      network: network.id,
      currency: 'USDT',
      sender_wallet: manualSenderWallet,
      receiver_wallet: network.wallet_address,
      transaction_hash: txHash,
      admin_assisted: isAdminAssisted,
      admin_email: isAdminAssisted ? adminSession.adminEmail : null,
      is_gift_purchase: isGiftPurchase,
      purchased_by_user_id: isGiftPurchase ? purchaserUserId : null
    });

    try {
      console.log('💾 CRYPTO PAYMENT: Using API endpoint for payment submission');

      // Build transaction notes with all metadata
      const transactionNotes = {
        ...(isGiftPurchase && {
          is_gift_purchase: true,
          purchased_by_user_id: purchaserUserId,
          purchased_by_username: user?.username || user?.database_user?.username,
          recipient_username: recipientUsername,
          recipient_name: recipientValidation.recipientName
        }),
        ...(isAdminAssisted && {
          admin_assisted: true,
          admin_email: adminSession.adminEmail
        }),
        ...(useCommission && commissionApplied > 0 && {
          commission_used: commissionApplied,
          remaining_payment: remainingPayment,
          total_purchase_amount: totalCost
        }),
        // Balance payment metadata (for underpaid payments)
        ...(isBalancePayment && originalPaymentId && {
          is_balance_payment: true,
          original_payment_id: originalPaymentId,
          balance_amount: balanceOwed || remainingPayment
        }),
        // Birthday 2026 package tag — read by api/admin/approve-payment.js to
        // issue the commission-free buyer + upline gifts on approval.
        ...(birthdayPackage && {
          birthday_package: birthdayPackage,
          birthday_qty: birthdayQty || 1
        }),
        ...(productionCapitalPackage && {
          campaign_id: 'PRODUCTION_CAPITAL_2026',
          campaign_package: productionCapitalPackage,
          campaign_package_qty: productionCapitalPackageQty || 1,
          campaign_cart_explicit: true,
          campaign_packages: (productionCapitalPackages && productionCapitalPackages.length
            ? productionCapitalPackages
            : [{ packageId: productionCapitalPackage, qty: productionCapitalPackageQty || 1 }])
        })
      };

      // Only create crypto payment record if there's a remaining payment (topup)
      // If fully covered by commission, we skip the crypto payment record
      let paymentResult: any = null;

      if (remainingPayment > 0) {
        const paymentData = {
          user_id: recipientUserId, // Shares go to recipient
          amount: remainingPayment, // Only the topup amount
          total_amount: totalCost, // For commission tracking
          shares_to_purchase: shares,
          network: network.id,
          currency: 'USDT',
          sender_wallet: manualSenderWallet,
          receiver_wallet: network.wallet_address,
          transaction_hash: txHash,
          screenshot_url: screenshotUrl, // MANDATORY - server rejects without proof
          status: 'pending',
          // Add admin assistance metadata
          admin_assisted: isAdminAssisted,
          // Store all metadata in transaction_notes JSON field
          transaction_notes: Object.keys(transactionNotes).length > 0 ? JSON.stringify(transactionNotes) : null
        };

        // Submit via API endpoint (handles commission deduction server-side)
        const response = await fetch('/api/purchase/submit-crypto-payment', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...getSessionHeaders() },
          body: JSON.stringify({
            paymentData,
            useCommission,
            commissionApplied,
            purchaserUserId,
            currentPhaseId: currentPhase?.id,
            // v4.8.115: attribution — only sent for a COMPANY purchase, so the
            // PERSONAL body stays byte-identical to before (backend re-validates).
            ...(purchaserType === 'COMPANY' && selectedCompanyId
              ? { purchaserType: 'COMPANY', companyId: selectedCompanyId }
              : {})
          })
        });

        const result = await response.json();

        console.log('💾 CRYPTO PAYMENT: API response:', result);

        if (!response.ok || !result.success) {
          console.error('❌ CRYPTO PAYMENT: API error:', result.error);

          // v2.9.95: Check for duplicate transaction hash error — match actual server 409 response text
          if (result.error?.includes('already been used') || result.error?.includes('already been submitted')) {
            throw new Error('This transaction hash has already been submitted. Please check your transaction history or use a different transaction.');
          }

          throw new Error('Failed to process payment: ' + (result.error || 'Unknown error'));
        }

        paymentResult = result.payment;
        console.log('✅ CRYPTO PAYMENT: Payment record created successfully:', paymentResult);

        // Trigger BSC auto-verification for eligible users (currently user 106 only)
        if (network.id === 'BSC' && paymentResult?.id) {
          console.log('🔍 CRYPTO PAYMENT: Checking BSC auto-verification eligibility...');

          // Check if user is eligible (from env config)
          const enabledUserIds = (import.meta.env.VITE_BSC_AUTO_VERIFY_ENABLED_USER_IDS || '106')
            .split(',')
            .map((id: string) => parseInt(id.trim()))
            .filter((id: number) => !isNaN(id));

          if (enabledUserIds.includes(recipientUserId)) {
            console.log('🔍 CRYPTO PAYMENT: User eligible for BSC auto-verification, triggering...');

            // Trigger async verification (don't await - let it run in background)
            triggerBscVerification(
              paymentResult.id,
              txHash,
              recipientUserId,
              remainingPayment,
              manualSenderWallet,
              network.wallet_address
            ).catch((verifyError) => {
              console.error('⚠️ CRYPTO PAYMENT: BSC verification error (non-blocking):', verifyError);
            });
          } else {
            console.log('🔍 CRYPTO PAYMENT: User not eligible for BSC auto-verification');
          }
        }
      } else {
        console.log('💰 CRYPTO PAYMENT: Purchase fully covered by commission - no crypto payment record needed');
      }

      // Create share purchase record via API
      const sharePurchaseData = {
        user_id: recipientUserId,
        package_name: currentPhase ? `${currentPhase.phase_name} - Crypto Purchase` : 'Crypto Purchase',
        shares_purchased: totalShares || shares, // Total shares including bonus
        bonus_shares: bonusShares || 0,
        total_amount: totalCost,
        commission_used: commissionApplied,
        remaining_payment: remainingPayment,
        payment_method: remainingPayment > 0 ? 'crypto' : 'commission',
        status: remainingPayment > 0 ? 'pending' : 'active', // Active if fully paid by commission
        phase_id: currentPhase?.id || null,
        // v2.9.98: Include transaction hash for idempotency — prevents duplicate share records on retry
        transaction_reference: txHash || null
      };

      console.log('💾 CRYPTO PAYMENT: Creating share purchase record via API');

      const sharePurchaseResponse = await fetch('/api/purchase/submit-crypto-payment', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getSessionHeaders() },
        body: JSON.stringify({
          sharePurchaseData,
          giftRecipientUsername: isGiftPurchase ? recipientUsername : null,
          // If fully paid by commission, also update user's total_shares
          updateUserShares: remainingPayment === 0 ? {
            userId: recipientUserId,
            sharesToAdd: totalShares || shares
          } : null,
          // v4.8.115: attribution — only sent for a COMPANY purchase (PERSONAL body
          // stays byte-identical to before; backend re-validates and is authoritative).
          ...(purchaserType === 'COMPANY' && selectedCompanyId
            ? { purchaserType: 'COMPANY', companyId: selectedCompanyId }
            : {})
        })
      });

      const sharePurchaseResult = await sharePurchaseResponse.json();

      if (!sharePurchaseResponse.ok || !sharePurchaseResult.success) {
        console.error('❌ CRYPTO PAYMENT: Share purchase creation error:', sharePurchaseResult.error);
        throw new Error('Failed to create share purchase record: ' + (sharePurchaseResult.error || 'Unknown error'));
      }

      console.log('✅ CRYPTO PAYMENT: Share purchase record created:', sharePurchaseResult.sharePurchase);

      return sharePurchaseResult.sharePurchase;
    } catch (error: any) {
      console.error('❌ CRYPTO PAYMENT: Exception in createPaymentRecord:', error);
      console.error('❌ CRYPTO PAYMENT: Exception details:', {
        message: error?.message,
        stack: error?.stack,
        name: error?.name
      });
      throw error;
    }
  };



  return (
    <div>
      <h2 style={{ fontSize: '20px', fontWeight: 'bold', marginBottom: '20px', textAlign: 'center' }}>
        {isBalancePayment ? `💳 Balance Payment (${network.name})` : `${network.icon} ${network.name} Payment`}
      </h2>

      {/* Balance Payment Notice Banner */}
      {isBalancePayment && (
        <div style={{
          backgroundColor: 'rgba(245, 158, 11, 0.2)',
          border: '2px solid #f59e0b',
          borderRadius: '12px',
          padding: '16px',
          marginBottom: '20px'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '12px' }}>
            <div style={{ width: '36px', height: '36px', background: '#f59e0b', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '18px' }}>
              ⚠️
            </div>
            <div style={{ color: '#f59e0b', fontSize: '16px', fontWeight: 'bold' }}>
              Balance Payment Required
            </div>
          </div>
          <div style={{ color: '#fbbf24', fontSize: '14px', lineHeight: '1.6' }}>
            <p style={{ margin: '0 0 8px 0' }}>
              This is a <strong>balance payment</strong> for an underpaid transaction.
            </p>
            <p style={{ margin: '0 0 8px 0' }}>
              <strong>Balance Due:</strong> ${(balanceOwed || 0).toFixed(2)} USDT
            </p>
            <p style={{ margin: 0, fontSize: '13px', color: '#ffffff' }}>
              Send exactly this amount to complete your original purchase.
            </p>
          </div>
        </div>
      )}

      {/* Special Pricing Banner for User 106 */}
      {isSpecialPricingActive && (
        <div style={{
          backgroundColor: 'rgba(245, 158, 11, 0.15)',
          border: '2px solid #f59e0b',
          borderRadius: '12px',
          padding: '16px',
          marginBottom: '20px',
          textAlign: 'center'
        }}>
          <div style={{ color: '#f59e0b', fontSize: '16px', fontWeight: 'bold', marginBottom: '8px' }}>
            🎯 SPECIAL TEST PRICING ACTIVE
          </div>
          <div style={{ color: '#fbbf24', fontSize: '14px' }}>
            $1.00 for 1 share (normally ${normalCost.toFixed(2)})
          </div>
        </div>
      )}

      {/* Payment Instructions */}
      <div style={{
        backgroundColor: 'rgba(34, 197, 94, 0.1)',
        border: '1px solid rgba(34, 197, 94, 0.3)',
        borderRadius: '12px',
        padding: '20px',
        marginBottom: '20px'
      }}>
        <h3 style={{ color: '#22c55e', fontWeight: 'bold', margin: '0 0 16px 0' }}>
          💰 PAYMENT INSTRUCTIONS:
        </h3>
        <div style={{ color: '#ffffff', fontSize: '14px', lineHeight: '1.6' }}>
          <p style={{ margin: '0 0 8px 0' }}>• Total Purchase: ${totalCost.toFixed(2)} USDT{isSpecialPricingActive && ` (Special test pricing!)`}</p>
          {useCommission && commissionApplied > 0 && (
            <>
              <p style={{ margin: '0 0 8px 0', color: '#22c55e' }}>• Commission Applied: -${commissionApplied.toFixed(2)} USDT</p>
              <p style={{ margin: '0 0 8px 0', fontWeight: 'bold', color: '#fbbf24' }}>• Amount to Pay: ${remainingPayment.toFixed(2)} USDT</p>
            </>
          )}
          {(!useCommission || commissionApplied === 0) && (
            <p style={{ margin: '0 0 8px 0' }}>• Amount to Pay: ${totalCost.toFixed(2)} USDT</p>
          )}
          <p style={{ margin: '0 0 8px 0' }}>• Shares: {shares} {bonusShares > 0 && `(+${bonusShares} bonus = ${totalShares} total)`}</p>
          <PurchaseRewardPreview
            productionCapitalPackages={productionCapitalPackages}
            productionCapitalPackage={productionCapitalPackage}
            productionCapitalPackageQty={productionCapitalPackageQty}
            birthdayPackage={birthdayPackage}
            birthdayQty={birthdayQty}
            phasePrice={currentPhase?.price_per_share}
            paidShares={shares}
            tierBonusShares={bonusShares}
            singleShareActive={singleShareActive}
          />
          <p style={{ margin: '0 0 8px 0' }}>• Network: {network.technical}</p>
          {remainingPayment > 0 ? (
            <>
              <p style={{ margin: '0 0 8px 0' }}>• Send from your exchange/wallet to the address below</p>
              <p style={{ margin: '0' }}>• Enter your sending wallet address and transaction hash</p>
            </>
          ) : (
            <p style={{ margin: '0', color: '#22c55e', fontWeight: 'bold' }}>✅ Purchase fully covered by commission balance!</p>
          )}
        </div>
      </div>

      {/* Company Wallet Address */}
      <div style={{ marginBottom: '20px' }}>
        <label style={{ display: 'block', color: '#ffffff', marginBottom: '8px', fontSize: '14px' }}>
          Company Wallet Address ({network.technical})
        </label>
        <WalletQRCode walletAddress={network.wallet_address} size={180} amount={remainingPayment} />
        <div style={{
          display: 'flex',
          backgroundColor: 'rgba(55, 65, 81, 0.5)',
          borderRadius: '8px',
          border: '2px solid #374151'
        }}>
          <input
            type="text"
            value={network.wallet_address}
            readOnly
            style={{
              flex: 1,
              padding: '12px',
              backgroundColor: 'transparent',
              border: 'none',
              color: 'white',
              fontSize: '14px',
              fontFamily: 'monospace'
            }}
          />
          <button
            onClick={() => copyToClipboard(network.wallet_address)}
            style={{
              padding: '12px 16px',
              backgroundColor: walletCopied ? '#22c55e' : '#3b82f6',
              border: 'none',
              borderRadius: '0 6px 6px 0',
              color: 'white',
              fontSize: '14px',
              cursor: 'pointer',
              transition: 'background-color 0.2s',
              minWidth: '90px'
            }}
          >
            {walletCopied ? '✓ Copied' : 'Copy'}
          </button>
        </div>
        <button
          onClick={generateShareablePaymentQR}
          disabled={isGeneratingShareQR}
          style={{
            marginTop: '10px',
            width: '100%',
            padding: '10px 16px',
            background: 'linear-gradient(135deg, #92400e, #78350f)',
            border: '1px solid #d4af37',
            borderRadius: '8px',
            color: '#fbbf24',
            fontSize: '14px',
            fontWeight: 'bold',
            cursor: isGeneratingShareQR ? 'wait' : 'pointer',
            opacity: isGeneratingShareQR ? 0.6 : 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px'
          }}
        >
          {isGeneratingShareQR ? '⏳ Generating...' : '📤 Share Payment QR'}
        </button>
      </div>

      {/* Manual Payment Form */}
      <div style={{ marginBottom: '20px' }}>
        <div style={{
          backgroundColor: 'rgba(34, 197, 94, 0.1)',
          border: '1px solid rgba(34, 197, 94, 0.3)',
          borderRadius: '12px',
          padding: '16px',
          marginBottom: '20px'
        }}>
          <h4 style={{ color: '#22c55e', fontWeight: 'bold', margin: '0 0 8px 0', fontSize: '14px' }}>
            💡 EXCHANGE PAYMENT INSTRUCTIONS:
          </h4>
          <div style={{ color: '#ffffff', fontSize: '13px', lineHeight: '1.5' }}>
            <p style={{ margin: '0 0 4px 0' }}>1. Copy the company wallet address above</p>
            <p style={{ margin: '0 0 4px 0' }}>2. Send {remainingPayment.toFixed(2)} USDT from your exchange/wallet</p>
            <p style={{ margin: '0 0 4px 0' }}>3. Enter your sending wallet address and transaction hash below</p>
            <p style={{ margin: '0' }}>4. Submit for admin verification</p>
          </div>
        </div>

          {/* Sender Wallet Address */}
          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', color: '#ffffff', marginBottom: '8px', fontSize: '14px' }}>
              Your Wallet Address (where you sent from) *
            </label>
            <input
              type="text"
              value={senderWallet}
              onChange={(e) => handleSenderWalletChange(e.target.value)}
              placeholder="0x... (BSC/ETH) or T... (TRON)"
              disabled={isProcessingPayment}
              style={{
                width: '100%',
                padding: '12px',
                backgroundColor: 'rgba(55, 65, 81, 0.5)',
                border: senderWallet && !isValidWalletAddress(senderWallet) ? '2px solid #ef4444'
                  : senderWallet && isValidWalletAddress(senderWallet) && network?.wallet_address && senderWallet.trim().toLowerCase() === network.wallet_address.toLowerCase() ? '2px solid #ef4444'
                  : senderWallet && isValidWalletAddress(senderWallet) ? '2px solid #22c55e'
                  : '2px solid #374151',
                borderRadius: '8px',
                color: 'white',
                fontSize: '14px',
                fontFamily: 'monospace',
                opacity: isProcessingPayment ? 0.6 : 1,
                cursor: isProcessingPayment ? 'not-allowed' : 'text'
              }}
            />
            {senderWallet && !isValidWalletAddress(senderWallet) && (
              <p style={{ color: '#f87171', fontSize: '12px', margin: '4px 0 0 0' }}>
                ⚠️ Invalid format. Must be 0x + 40 hex chars (BSC/ETH) or T + 33 chars (TRON). No spaces allowed.
              </p>
            )}
            {senderWallet && isValidWalletAddress(senderWallet) && network?.wallet_address && senderWallet.trim().toLowerCase() === network.wallet_address.toLowerCase() && (
              <p style={{ color: '#f87171', fontSize: '12px', margin: '4px 0 0 0' }}>
                ⚠️ This is the company receiving address. Enter YOUR wallet address — the one you sent payment FROM.
              </p>
            )}
            {senderWallet && isValidWalletAddress(senderWallet) && !(network?.wallet_address && senderWallet.trim().toLowerCase() === network.wallet_address.toLowerCase()) && (
              <p style={{ color: '#22c55e', fontSize: '12px', margin: '4px 0 0 0' }}>✅ Valid address format</p>
            )}
          </div>

          {/* Transaction Hash */}
          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', color: '#ffffff', marginBottom: '8px', fontSize: '14px' }}>
              Transaction Hash *
            </label>
            <input
              type="text"
              value={manualTxHash}
              onChange={(e) => handleTxHashChange(e.target.value)}
              placeholder="0x... or 64-character hex hash"
              disabled={isProcessingPayment}
              style={{
                width: '100%',
                padding: '12px',
                backgroundColor: 'rgba(55, 65, 81, 0.5)',
                border: manualTxHash && !isValidTxHash(manualTxHash) ? '2px solid #ef4444' : manualTxHash && isValidTxHash(manualTxHash) ? '2px solid #22c55e' : '2px solid #374151',
                borderRadius: '8px',
                color: 'white',
                fontSize: '14px',
                fontFamily: 'monospace',
                opacity: isProcessingPayment ? 0.6 : 1,
                cursor: isProcessingPayment ? 'not-allowed' : 'text'
              }}
            />
            {manualTxHash && !isValidTxHash(manualTxHash) && (
              <p style={{ color: '#f87171', fontSize: '12px', margin: '4px 0 0 0' }}>
                ⚠️ Invalid format. Must be 0x + 64 hex chars or 64 hex chars. No spaces allowed.
              </p>
            )}
            {manualTxHash && isValidTxHash(manualTxHash) && (
              <p style={{ color: '#22c55e', fontSize: '12px', margin: '4px 0 0 0' }}>✅ Valid hash format</p>
            )}

            {/* Error Display - Right below transaction hash */}
            {error && (
              <div style={{
                backgroundColor: 'rgba(239, 68, 68, 0.1)',
                border: '1px solid rgba(239, 68, 68, 0.3)',
                borderRadius: '8px',
                padding: '12px',
                marginTop: '12px'
              }}>
                <p style={{ color: '#f87171', fontSize: '14px', margin: 0, fontWeight: '600' }}>
                  ❌ {error}
                </p>
              </div>
            )}
          </div>
        </div>

      {/* Processing Progress */}
      {uploadProgress && (
        <div style={{
          backgroundColor: 'rgba(59, 130, 246, 0.1)',
          border: '1px solid rgba(59, 130, 246, 0.3)',
          borderRadius: '8px',
          padding: '12px',
          marginBottom: '20px',
          color: '#60a5fa',
          display: 'flex',
          alignItems: 'center',
          gap: '10px'
        }}>
          <div style={{
            width: '20px',
            height: '20px',
            border: '3px solid rgba(59, 130, 246, 0.3)',
            borderTop: '3px solid #60a5fa',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite'
          }} />
          {uploadProgress}
        </div>
      )}

      {/* v4.8.115: Purchaser Capacity Section (Personal vs Company).
          Only shown when the user is the primary director of at least one company.
          Company and gift are mutually exclusive. */}
      {companies.length > 0 && (
        <div style={{
          backgroundColor: 'rgba(168, 85, 247, 0.08)',
          border: '1px solid rgba(168, 85, 247, 0.3)',
          borderRadius: '12px',
          padding: '16px',
          marginBottom: '20px'
        }}>
          <label style={{ display: 'block', color: '#c084fc', fontSize: '15px', fontWeight: '600', marginBottom: '10px' }}>
            🏢 Who are these shares for?
          </label>
          <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
            <button
              type="button"
              disabled={isProcessingPayment}
              onClick={() => { setPurchaserType('PERSONAL'); setSelectedCompanyId(''); }}
              style={{
                flex: '1 1 45%',
                padding: '10px 12px',
                borderRadius: '8px',
                border: purchaserType === 'PERSONAL' ? '2px solid #c084fc' : '1px solid rgba(255,255,255,0.2)',
                backgroundColor: purchaserType === 'PERSONAL' ? 'rgba(168, 85, 247, 0.2)' : 'transparent',
                color: '#ffffff',
                cursor: isProcessingPayment ? 'not-allowed' : 'pointer',
                fontWeight: purchaserType === 'PERSONAL' ? 700 : 500
              }}
            >
              Myself (Personal)
            </button>
            <button
              type="button"
              disabled={isProcessingPayment}
              onClick={() => {
                // Selecting COMPANY clears any gift selection (mutual exclusion).
                setPurchaserType('COMPANY');
                setIsGiftPurchase(false);
                setRecipientUsername('');
                setRecipientValidation({ isValidating: false, isValid: null, recipientName: null, recipientId: null });
              }}
              style={{
                flex: '1 1 45%',
                padding: '10px 12px',
                borderRadius: '8px',
                border: purchaserType === 'COMPANY' ? '2px solid #c084fc' : '1px solid rgba(255,255,255,0.2)',
                backgroundColor: purchaserType === 'COMPANY' ? 'rgba(168, 85, 247, 0.2)' : 'transparent',
                color: '#ffffff',
                cursor: isProcessingPayment ? 'not-allowed' : 'pointer',
                fontWeight: purchaserType === 'COMPANY' ? 700 : 500
              }}
            >
              A Company I direct
            </button>
          </div>

          {purchaserType === 'COMPANY' && (
            <div style={{ marginTop: '12px', paddingTop: '12px', borderTop: '1px solid rgba(168, 85, 247, 0.2)' }}>
              <label style={{ display: 'block', color: '#ffffff', marginBottom: '8px', fontSize: '14px' }}>
                Select the company purchasing these shares: *
              </label>
              <select
                value={selectedCompanyId}
                onChange={(e) => setSelectedCompanyId(e.target.value)}
                disabled={isProcessingPayment || companiesLoading}
                style={{
                  width: '100%',
                  padding: '10px',
                  borderRadius: '8px',
                  border: '1px solid rgba(255,255,255,0.2)',
                  backgroundColor: '#1f2937',
                  color: '#ffffff',
                  fontSize: '14px'
                }}
              >
                <option value="">— Select a company —</option>
                {companies.map((c) => {
                  const eligible = isCompanyEligible(c);
                  return (
                    <option key={c.id} value={c.id} disabled={!eligible}>
                      {c.legal_name}{eligible ? '' : ' (not eligible — KYB/active/frozen)'}
                    </option>
                  );
                })}
              </select>
              <p style={{ color: '#ffffff', fontSize: '14px', marginTop: '8px', marginBottom: 0 }}>
                💡 Shares will be attributed to the selected company. Only the company's
                primary director can purchase, and the company must be KYB-approved and active.
              </p>
            </div>
          )}
        </div>
      )}

      {/* Gift Purchase Section */}
      <div style={{
        backgroundColor: 'rgba(59, 130, 246, 0.1)',
        border: '1px solid rgba(59, 130, 246, 0.3)',
        borderRadius: '12px',
        padding: '16px',
        marginBottom: '20px'
      }}>
        <div style={{ marginBottom: '12px' }}>
          <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', color: '#60a5fa', fontSize: '15px', fontWeight: '600' }}>
            <input
              type="checkbox"
              checked={isGiftPurchase}
              onChange={(e) => {
                setIsGiftPurchase(e.target.checked);
                if (!e.target.checked) {
                  setRecipientUsername('');
                  setRecipientValidation({
                    isValidating: false,
                    isValid: null,
                    recipientName: null,
                    recipientId: null
                  });
                }
              }}
              disabled={isProcessingPayment || purchaserType === 'COMPANY'}
              style={{
                marginRight: '8px',
                width: '18px',
                height: '18px',
                cursor: purchaserType === 'COMPANY' ? 'not-allowed' : 'pointer'
              }}
            />
            🎁 Are you purchasing these shares for someone else?
          </label>
          {purchaserType === 'COMPANY' && (
            <p style={{ color: '#9ca3af', fontSize: '13px', margin: '6px 0 0 26px' }}>
              Gifting is disabled while purchasing in company capacity.
            </p>
          )}
        </div>

        {isGiftPurchase && (
          <div style={{ marginTop: '12px', paddingTop: '12px', borderTop: '1px solid rgba(59, 130, 246, 0.2)' }}>
            <label style={{ display: 'block', color: '#ffffff', marginBottom: '8px', fontSize: '14px' }}>
              Enter the username of the person you're purchasing for: *
            </label>
            <ReferralAutocomplete
              id="crypto-gift-recipient"
              name="recipientUsername"
              value={recipientUsername}
              onChange={(e) => handleRecipientUsernameChange(e.target.value)}
              placeholder="recipient_username"
              autoComplete="off"
              disabled={isProcessingPayment}
              validation={
                recipientValidation.isValidating
                  ? { isValid: false, isChecking: true, message: 'Checking username...', type: 'checking' }
                  : recipientValidation.isValid === true
                  ? { isValid: true, isChecking: false, message: `User found: ${recipientValidation.recipientName ?? ''}`, type: 'success' }
                  : recipientValidation.isValid === false && recipientUsername.trim()
                  ? { isValid: false, isChecking: false, message: recipientValidation.recipientName || `Username "${recipientUsername}" not found. Please check the spelling and try again.`, type: 'error' }
                  : null
              }
            />

            <p style={{ color: '#ffffff', fontSize: '14px', marginTop: '8px', marginBottom: 0 }}>
              💡 The shares will be allocated to this user's account. Commissions will be calculated based on their sponsor tree.
            </p>
          </div>
        )}
      </div>

      {/* Action Buttons */}
      <div style={{ display: 'flex', gap: '12px', marginTop: '30px' }}>
        <button
          onClick={onBack}
          disabled={isProcessingPayment}
          style={{
            flex: 1,
            padding: '12px',
            backgroundColor: '#ffffff',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            fontSize: '16px',
            cursor: isProcessingPayment ? 'not-allowed' : 'pointer',
            opacity: isProcessingPayment ? 0.5 : 1
          }}
        >
          Back
        </button>

        {(() => {
          // v7.3.345: Submit button active/hover styling fix
          // Previous logic required `proofFile`, but the proof-of-payment upload was removed
          // (see `const screenshotUrl = null; // Proof of payment upload removed`), so the
          // button was permanently styled as disabled even when the form was valid.
          // Now the button looks active whenever wallet + tx hash are both in valid formats,
          // matching the green ✅ indicators shown under each input.
          const walletValid = isValidWalletAddress(senderWallet.trim());
          const hashValid = isValidTxHash(manualTxHash.trim());
          const isCompanyWallet = !!(network?.wallet_address && senderWallet.trim().toLowerCase() === network.wallet_address.toLowerCase());
          const isFormValid = walletValid && hashValid && !isCompanyWallet;
          const isDisabled = !isFormValid || isProcessingPayment;
          return (
            <button
              type="button"
              onClick={handleShowConfirmation}
              disabled={isDisabled}
              onMouseEnter={() => setIsSubmitHovered(true)}
              onMouseLeave={() => setIsSubmitHovered(false)}
              style={{
                flex: 2,
                padding: '14px 20px',
                backgroundColor: isProcessingPayment
                  ? '#059669'
                  : isFormValid
                    ? (isSubmitHovered ? '#059669' : '#10b981')
                    : '#6b7280',
                color: 'white',
                border: 'none',
                borderRadius: '10px',
                fontSize: '16px',
                fontWeight: 700,
                letterSpacing: '0.3px',
                cursor: isDisabled ? 'not-allowed' : 'pointer',
                opacity: isDisabled ? 0.55 : 1,
                boxShadow: isFormValid && !isProcessingPayment
                  ? (isSubmitHovered
                      ? '0 8px 20px rgba(16, 185, 129, 0.45), 0 0 0 1px rgba(16, 185, 129, 0.6) inset'
                      : '0 4px 12px rgba(16, 185, 129, 0.35), 0 0 0 1px rgba(16, 185, 129, 0.5) inset')
                  : 'none',
                transform: isFormValid && isSubmitHovered && !isProcessingPayment ? 'translateY(-1px)' : 'translateY(0)',
                transition: 'background-color 150ms ease, box-shadow 150ms ease, transform 150ms ease, opacity 150ms ease',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px'
              }}
            >
          {isProcessingPayment ? (
            <>
              <div style={{
                width: '16px',
                height: '16px',
                border: '2px solid transparent',
                borderTop: '2px solid white',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite'
              }}></div>
              Submitting...
            </>
          ) : (
            `Submit Payment (${remainingPayment.toFixed(2)} USDT)`
          )}
            </button>
          );
        })()}
      </div>

      {/* Confirmation Modal */}
      {showConfirmation && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.75)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: '20px'
        }}>
          <div style={{
            backgroundColor: '#1f2937',
            borderRadius: '12px',
            padding: '32px',
            maxWidth: '500px',
            width: '100%',
            border: '1px solid #374151',
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.5)'
          }}>
            <h3 style={{
              fontSize: '24px',
              fontWeight: 'bold',
              color: 'white',
              marginBottom: '24px',
              textAlign: 'center'
            }}>
              ⚠️ Confirm Your Purchase
            </h3>

            <div style={{
              backgroundColor: '#111827',
              borderRadius: '8px',
              padding: '20px',
              marginBottom: '24px',
              border: '2px solid #fbbf24'
            }}>
              <p style={{
                color: '#ffffff',
                fontSize: '16px',
                marginBottom: '16px',
                textAlign: 'center'
              }}>
                You are purchasing:
              </p>

              <div style={{
                textAlign: 'center',
                marginBottom: '12px'
              }}>
                <div style={{
                  fontSize: '36px',
                  fontWeight: 'bold',
                  color: '#fbbf24',
                  marginBottom: '4px'
                }}>
                  {shares} {shares === 1 ? 'Share' : 'Shares'}
                </div>
                <PurchaseRewardPreview
                  productionCapitalPackages={productionCapitalPackages}
                  productionCapitalPackage={productionCapitalPackage}
                  productionCapitalPackageQty={productionCapitalPackageQty}
                  birthdayPackage={birthdayPackage}
                  birthdayQty={birthdayQty}
                  phasePrice={currentPhase?.price_per_share}
                  paidShares={shares}
                  tierBonusShares={bonusShares}
                  singleShareActive={singleShareActive}
                />
                <div style={{
                  fontSize: '24px',
                  color: '#10b981',
                  fontWeight: '600'
                }}>
                  ${totalCost.toFixed(2)} USD
                </div>
                {useCommission && commissionApplied > 0 && (
                  <div style={{
                    marginTop: '12px',
                    padding: '8px',
                    backgroundColor: 'rgba(16, 185, 129, 0.1)',
                    borderRadius: '6px'
                  }}>
                    <div style={{
                      color: '#10b981',
                      fontSize: '14px',
                      marginBottom: '4px'
                    }}>
                      Commission Applied: -${commissionApplied.toFixed(2)}
                    </div>
                    <div style={{
                      color: '#fbbf24',
                      fontSize: '16px',
                      fontWeight: 'bold'
                    }}>
                      Amount to Pay: ${remainingPayment.toFixed(2)}
                    </div>
                  </div>
                )}
              </div>

              <div style={{
                borderTop: '1px solid #374151',
                paddingTop: '12px',
                marginTop: '12px'
              }}>
                <div style={{
                  color: '#ffffff',
                  fontSize: '14px',
                  marginBottom: '4px'
                }}>
                  Network: <span style={{ color: '#60a5fa' }}>{network.name}</span>
                </div>
                <div style={{
                  color: '#ffffff',
                  fontSize: '14px',
                  wordBreak: 'break-all'
                }}>
                  Transaction: <span style={{ color: '#60a5fa', fontFamily: 'monospace', fontSize: '14px' }}>{manualTxHash.substring(0, 20)}...</span>
                </div>
              </div>

              {/* Gift Purchase Info */}
              {isGiftPurchase && recipientValidation.recipientName && (
                <div style={{
                  borderTop: '1px solid #374151',
                  paddingTop: '12px',
                  marginTop: '12px',
                  backgroundColor: 'rgba(59, 130, 246, 0.1)',
                  padding: '12px',
                  borderRadius: '6px',
                  marginLeft: '-12px',
                  marginRight: '-12px',
                  marginBottom: '-12px'
                }}>
                  <div style={{
                    color: '#60a5fa',
                    fontSize: '14px',
                    fontWeight: '600',
                    marginBottom: '8px',
                    textAlign: 'center'
                  }}>
                    🎁 Gift Purchase
                  </div>
                  <div style={{
                    color: '#ffffff',
                    fontSize: '13px',
                    marginBottom: '4px'
                  }}>
                    Purchasing for: <span style={{ color: '#fbbf24', fontWeight: '600' }}>{recipientValidation.recipientName}</span>
                  </div>
                  <div style={{
                    color: '#ffffff',
                    fontSize: '13px'
                  }}>
                    Payment from: <span style={{ color: '#10b981', fontWeight: '600' }}>{user?.full_name || user?.database_user?.full_name || user?.username || 'You'}</span>
                  </div>
                </div>
              )}
            </div>

            <p style={{
              color: '#ffffff',
              fontSize: '14px',
              marginBottom: '24px',
              textAlign: 'center',
              lineHeight: '1.5'
            }}>
              Please verify the details above are correct before confirming. This action cannot be undone.
            </p>

            <div style={{
              display: 'flex',
              gap: '12px'
            }}>
              <button
                onClick={handleManualPayment}
                style={{
                  width: '100%',
                  padding: '12px',
                  backgroundColor: '#10b981',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '16px',
                  cursor: 'pointer',
                  fontWeight: '600'
                }}
              >
                Confirm & Submit
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default CryptoPaymentStep;
